/** Shared home destinations and local-time behavior across desktop and native. */
export type HomeLink = {
  name: string;
  url: string;
  group: "work" | "ai";
  icon?: string;
  iconUrl?: string;
};
export const HOME_LINKS: HomeLink[] = [
  { icon: "teams", name: "Teams", url: "https://teams.microsoft.com", group: "work" },
  { icon: "github", name: "GitHub", url: "https://github.com", group: "work" },
  { icon: "linear", name: "Linear", url: "https://linear.app", group: "work" },
  {
    icon: "google-cloud",
    name: "Google Cloud",
    url: "https://console.cloud.google.com",
    group: "work",
  },
  { icon: "gotomypc", name: "GoToMyPC", url: "https://www.gotomypc.com", group: "work" },
  { icon: "perplexity", name: "Perplexity", url: "https://www.perplexity.ai", group: "work" },
  { icon: "grok", name: "Grok", url: "https://console.x.ai", group: "ai" },
  { icon: "claude", name: "Claude", url: "https://platform.claude.com", group: "ai" },
  { icon: "openai", name: "OpenAI", url: "https://platform.openai.com", group: "ai" },
  { icon: "ai-studio", name: "AI Studio", url: "https://aistudio.google.com", group: "ai" },
];
export function homeLinks(extra?: string): HomeLink[] {
  if (!extra) return HOME_LINKS;
  try {
    const values: unknown = JSON.parse(extra);
    if (!Array.isArray(values)) return HOME_LINKS;
    const links = values.filter(
      (v): v is HomeLink =>
        v &&
        typeof v.name === "string" &&
        typeof v.url === "string" &&
        /^https:\/\//.test(v.url) &&
        ["work", "ai"].includes(v.group) &&
        (v.iconUrl === undefined ||
          (typeof v.iconUrl === "string" && /^https:\/\//.test(v.iconUrl))),
    );
    return [...HOME_LINKS, ...links];
  } catch {
    return HOME_LINKS;
  }
}
export function greetingPeriod(date: Date): "morning" | "afternoon" | "evening" {
  const hour = date.getHours();
  return hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
}
export function homeClocks(date: Date) {
  const clock = (timeZone?: string) =>
    new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZone,
      timeZoneName: "short",
    }).format(date);
  return {
    local: clock(),
    eastern: clock("America/New_York"),
    date: new Intl.DateTimeFormat(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    }).format(date),
  };
}
export function primaryHomeBot<T extends { pinned: boolean; archivedAt?: string | null }>(
  bots: T[],
): T | undefined {
  const available = bots.filter((bot) => !bot.archivedAt);
  return available.find((bot) => bot.pinned) ?? available[0];
}
/** Public source fragments only; no runtime prompts, files, or credentials. */
export const HOME_CODE = [
  "const space = await rpc.spaces.list();",
  "const bot = primaryHomeBot(space.current.bots);",
  "await runThreadSubscription({ signal, onEvent });",
  "const context = createSpaceRpc(selectedSpaceId());",
  "return { terminal, open, close };",
  "const artifacts = await rpc.artifacts.listSpace({ limit: 60 });",
  "await turn.complete();  // ready for the next instruction",
  "export function useCallSession() { return useSyncExternalStore(subscribe, getSnapshot); }",
];
export const MAX_EDITOR_BYTES = 4 * 1024 * 1024;
export function editableTextFile(name: string) {
  return /\.(md|markdown|mdx|json|jsonc|txt|yaml|yml|toml|csv|log|js|ts|tsx|jsx|css|html|xml|sh)$/i.test(
    name,
  );
}

/** Bound plain-text previews before decoding their contents on either client. */
export function textFilePreview(name: string, mimeType: string, size: number): boolean {
  const mime = mimeType.split(";", 1)[0]!.trim().toLowerCase();
  return (
    size >= 0 &&
    size <= MAX_EDITOR_BYTES &&
    (editableTextFile(name) || mime.startsWith("text/") || mime === "application/json")
  );
}
